// /app/frontend/src/lib/firebaseAuth.js
// Auth: Google real por defecto en TODOS los entornos.
// El "DEV Preview" solo se habilita si REACT_APP_ENABLE_DEV_LOGIN === 'true'.
// En producción debe quedar siempre false (default).

import {
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  browserLocalPersistence,
  setPersistence,
} from "firebase/auth";

import { auth } from "./firebase";

// Provider
const googleProvider = new GoogleAuthProvider();
googleProvider.addScope("email");
googleProvider.addScope("profile");

// URLs (por si tu UI las usa)
export const PRODUCTION_URL = "https://giroexpress-bancked-taza.web.app";
export const FIREBASE_AUTH_HANDLER =
  "https://giroexpress-bancked-taza.firebaseapp.com/__/auth/handler";

// Bandera de control. Default OFF.
const ENABLE_DEV_LOGIN =
  String(process.env.REACT_APP_ENABLE_DEV_LOGIN || "false").toLowerCase() === "true";

// Detecta preview Emergent (solo informativo)
export function isPreviewEnvironment() {
  const host = window.location.hostname;
  return host.includes("preview.emergentagent.com") || host.includes("emergent.sh");
}

export function getEnvironmentDiagnostics() {
  return {
    host: window.location.hostname,
    href: window.location.href,
    isPreview: isPreviewEnvironment(),
    devLoginEnabled: ENABLE_DEV_LOGIN,
    userAgent: navigator.userAgent,
    isMobile: /Mobile|Android|iPhone/i.test(navigator.userAgent),
  };
}

// ========================
// DEV AUTH (opt-in: solo si ENABLE_DEV_LOGIN=true)
// ========================
function devUser() {
  return {
    uid: "dev-preview-user",
    email: "dev@preview.local",
    displayName: "DEV Preview",
    photoURL: null,
    isDev: true,
  };
}

const DEV_KEY = "__DEV_AUTH_USER__";

function loadDevUser() {
  if (!ENABLE_DEV_LOGIN) return null;
  try {
    const raw = localStorage.getItem(DEV_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveDevUser(u) {
  try {
    localStorage.setItem(DEV_KEY, JSON.stringify(u));
  } catch {}
}

function clearDevUser() {
  try {
    localStorage.removeItem(DEV_KEY);
  } catch {}
}

// Persistencia normal Firebase
async function setPersistenceLocal() {
  if (!auth) return;
  try {
    await setPersistence(auth, browserLocalPersistence);
  } catch (err) {
    console.warn("⚠️ Error estableciendo persistencia:", err);
  }
}

// ========================
// API PUBLICA
// ========================
export async function signInWithGoogle() {
  const env = getEnvironmentDiagnostics();
  console.log("🧪 signInWithGoogle - Diagnóstico:", env);

  // Solo si está explícitamente habilitado y estamos en preview
  if (ENABLE_DEV_LOGIN && env.isPreview) {
    const u = devUser();
    saveDevUser(u);
    return { success: true, user: u, dev: true };
  }

  // Google real (default en TODOS los entornos)
  if (!auth) {
    return {
      success: false,
      error: new Error("AUTH_NOT_INITIALIZED"),
      friendlyError: {
        message: "No se pudo iniciar sesión con Google.",
        hint: "Auth no está inicializado en este entorno.",
        canRetry: false,
      },
      errorCode: "AUTH_NOT_INITIALIZED",
    };
  }

  await setPersistenceLocal();

  // PASO 1 — Probar con POPUP en lugar de REDIRECT (preview web)
  try {
    const result = await signInWithPopup(auth, googleProvider);
    const u = result?.user;
    if (!u) {
      return {
        success: false,
        error: new Error("NO_USER_AFTER_POPUP"),
        friendlyError: {
          message: "No se pudo obtener el usuario tras Google.",
          hint: "Intenta nuevamente.",
          canRetry: true,
        },
        errorCode: "NO_USER_AFTER_POPUP",
      };
    }
    return {
      success: true,
      user: {
        uid: u.uid,
        email: u.email,
        displayName: u.displayName || u.email?.split("@")[0] || "Usuario",
        photoURL: u.photoURL,
      },
    };
  } catch (err) {
    console.error("❌ Error en signInWithPopup:", err);
    // Fallback: si el popup fue bloqueado/cerrado, usar redirect
    const code = err?.code || "";
    const popupBlocked =
      code === "auth/popup-blocked" ||
      code === "auth/popup-closed-by-user" ||
      code === "auth/cancelled-popup-request" ||
      code === "auth/operation-not-supported-in-this-environment";
    if (popupBlocked) {
      try {
        await signInWithRedirect(auth, googleProvider);
        return { success: true, redirecting: true };
      } catch (err2) {
        return {
          success: false,
          error: err2,
          friendlyError: {
            message: "No se pudo iniciar sesión con Google.",
            hint: err2?.message || "Intenta nuevamente.",
            canRetry: true,
          },
          errorCode: err2?.code || null,
        };
      }
    }
    return {
      success: false,
      error: err,
      friendlyError: {
        message: "No se pudo iniciar sesión con Google.",
        hint: err?.message || "Intenta nuevamente.",
        canRetry: true,
      },
      errorCode: code || null,
    };
  }
}

export async function handleRedirectResult() {
  // Si DEV login está habilitado y existe sesión DEV guardada → la usamos
  const dev = loadDevUser();
  if (dev) return dev;

  // Google real
  if (!auth) return null;
  try {
    const result = await getRedirectResult(auth);
    const u = result?.user;
    if (!u) return null;

    return {
      uid: u.uid,
      email: u.email,
      displayName: u.displayName || u.email?.split("@")[0] || "Usuario",
      photoURL: u.photoURL,
    };
  } catch (err) {
    console.error("❌ REDIRECT_RESULT:", err);
    return null;
  }
}

export async function logOut() {
  // Limpiar DEV (no-op si no hay)
  clearDevUser();
  // Producción: signOut real
  if (auth) {
    try { await signOut(auth); } catch (_) { /* noop */ }
  }
}

export function onAuthChange(callback) {
  // Si hay DEV user activo (solo cuando ENABLE_DEV_LOGIN=true), úsalo
  const dev = loadDevUser();
  if (dev) {
    callback(dev);
    return () => {};
  }

  // Google real
  if (!auth) {
    callback(null);
    return () => {};
  }
  return onAuthStateChanged(auth, (u) => {
    if (!u) return callback(null);
    callback({
      uid: u.uid,
      email: u.email,
      displayName: u.displayName || u.email?.split("@")[0] || "Usuario",
      photoURL: u.photoURL,
    });
  });
}

export { auth };
